<template>
  <div>
    <slot/>
  </div>
</template>

<script setup>
import Highcharts from "@/highcharts"
import { onMounted } from "vue"

const props = defineProps({
  list: Array,
  title: String,
})

onMounted(() => {
  const { title, list } = props

  const options = {
    chart: {
      type: "areaspline",
    },
    legend: {
      enabled: false,
    },
    title: {
      text: title,
    },
    xAxis: {
      title: {
        text: "LSAT Score",
      },
      allowDecimals: false,
      labels: {
        formatter() {
          const percentiles = [0, 0.003, 0.004, 0.004, 0.005, 0.007, 0.008, 0.01, 0.013, 0.017, 0.02, 0.025, 0.032, 0.037, 0.047, 0.056, 0.067, 0.081, 0.096, 0.114, 0.134, 0.152, 0.178, 0.205, 0.229, 0.261, 0.295, 0.33, 0.363, 0.403, 0.443, 0.481, 0.522, 0.556, 0.597, 0.639, 0.674, 0.709, 0.746, 0.776, 0.804, 0.834, 0.859, 0.881, 0.9, 0.92, 0.932, 0.946, 0.959, 0.967, 0.974, 0.98, 0.986, 0.99, 0.992, 0.994, 0.996, 0.998, 0.999, 0.999, 0.999]

          return `${this.value}<br>${Math.round(percentiles[this.value - 120] * 1000) / 10} %ile`
        },
      },
    },
    yAxis: {
      max: .5,
      title: {
        text: "",
      },
      labels: {
        formatter() {
          return ""
        },
      },
    },
    tooltip: {
      crosshairs: true,
      formatter() {
        let text = ""

        if (this.x > 151) {
          text = `<span>LSAT Score: ${this.x}</span><br> ${Math.round((1 - this.y) * 1000) / 10} percentile`
        } else {
          text = `<span>LSAT Score: ${this.x}</span><br> ${Math.round((this.y) * 1000) / 10} percentile`
        }

        return text
      },
    },
    plotOptions: {
      areaspline: {
        pointStart: 120,
        marker: {
          enabled: false,
          symbol: "circle",
          radius: 2,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
      },
    }, series: [{
      name: "LSAT Score",
      data: [0.000, 0.006, 0.007, 0.008, 0.009, 0.010, 0.012, 0.014, 0.017, 0.020, 0.023, 0.028, 0.032, 0.037, 0.045, 0.054, 0.064, 0.074, 0.087, 0.102, 0.119, 0.138, 0.160, 0.181, 0.210, 0.236, 0.263, 0.294, 0.329, 0.361, 0.395, 0.432, 0.467, 0.495, 0.458, 0.423, 0.387, 0.351, 0.317, 0.285, 0.252, 0.224, 0.195, 0.169, 0.146, 0.123, 0.104, 0.085, 0.070, 0.056, 0.044, 0.034, 0.026, 0.019, 0.014, 0.009, 0.007, 0.005, 0.003, 0.002, 0.001],
    }],
  }

  new Highcharts.chart("lsat_distribution", options, ((chart) => {
    const b_1 = 4
    const b_2 = 4
    const space_based_on_div_size = 855
    const p1 = 63
    const p2 = 810
    const corner = 0
    const rHeight = b_1
    const space = b_2
    let lastPosition = space_based_on_div_size + space
    const stroke = 0
    const zInd = 4

    const redColor = "rgb(0,0,0)"
    const greenColor = "rgb(19,255,0)"
    const sameColor = "rgb(206,35,221)"

    let text
    let textBG

    function showData() {
      const x = parseInt($(this).attr("x"), 10) + 0
      const y = parseInt($(this).attr("y"), 10) - 5
      const tip = `<span>${$(this).attr("data-name")}</span>`
      text = chart.renderer.text(tip, x - 100, y)
        .attr({ zIndex: 8 })
        .css({ color: "black", fontWeight: "bold", fontSize: "12px" })
        .add()
      const box = text.getBBox()
      // Now draw a box surrounding the tool tip
      textBG = chart.renderer.rect(box.x, box.y, box.width, box.height, 5)
        .attr({
          "fill": "#FFFFEF",
          "stroke": "gray",
          "stroke-width": 1,
          "zIndex": 7,
          "whitespace": "normal",
        })
        .add()
    }

    function closeData() {
      text.destroy()
      textBG.destroy()
    }

    list.forEach((item) => {
      const y1_L = item[0]
      const y2_L = item[1]
      const school = item[2]
      const p_change = item[3]

      if (y1_L > 0 && y2_L > 0) {
        lastPosition = lastPosition - space

        let name = `${school}: no change`

        if (y1_L > y2_L) { // went down
          name = `${school}: down ${y1_L - y2_L} points, ${p_change} percentile`
          chart.renderer.rect(
            chart.xAxis[0].toPixels(y2_L),
            lastPosition,
            chart.xAxis[0].toPixels(y1_L) - chart.xAxis[0].toPixels(y2_L),
            rHeight,
            corner,
          ).attr({
            "data-name": name,
            "stroke-width": stroke,
            "stroke": redColor,
            "fill": redColor,
            "zIndex": zInd,
          }).on("mouseover", showData).on("mouseout", closeData).add()
        } else if (y1_L < y2_L) { // went up
          name = `${school}: up ${y2_L - y1_L} points, ${p_change} percentile`
          chart.renderer.rect(
            chart.xAxis[0].toPixels(y1_L),
            lastPosition,
            chart.xAxis[0].toPixels(y2_L) - chart.xAxis[0].toPixels(y1_L),
            rHeight,
            corner,
          ).attr({
            "data-name": name,
            "stroke-width": stroke,
            "stroke": greenColor,
            "fill": greenColor,
            "zIndex": zInd,
          }).on("mouseover", showData).on("mouseout", closeData).add()
        }

        chart.renderer.rect(
          chart.xAxis[0].toPixels(y1_L) - 2,
          lastPosition,
          4,
          rHeight,
          corner,
        ).attr({
          "data-name": name,
          "stroke-width": stroke,
          "stroke": sameColor,
          "fill": sameColor,
          "zIndex": zInd,
        }).on("mouseover", showData).on("mouseout", closeData).add()
      }
    })

    let pt1 = 120
    let pt2 = 144.5
    chart.renderer.rect(chart.xAxis[0].toPixels(pt1), p1, chart.xAxis[0].toPixels(pt2) - chart.xAxis[0].toPixels(pt1), p2, 0).attr({ "data-name": name, "stroke-width": stroke, "stroke": "rgba(255,19,0,.8)", "fill": "rgba(255,19,0,.7)", "zIndex": 3 }).add()
    pt1 = 144.5
    pt2 = 146.5
    chart.renderer.rect(chart.xAxis[0].toPixels(pt1), p1, chart.xAxis[0].toPixels(pt2) - chart.xAxis[0].toPixels(pt1), p2, 0).attr({ "data-name": name, "stroke-width": stroke, "stroke": "rgba(255,19,0,.8)", "fill": "rgba(255,19,0,.6)", "zIndex": 3 }).add()
    pt1 = 146.5
    pt2 = 149.5
    chart.renderer.rect(chart.xAxis[0].toPixels(pt1), p1, chart.xAxis[0].toPixels(pt2) - chart.xAxis[0].toPixels(pt1), p2, 0).attr({ "data-name": name, "stroke-width": stroke, "stroke": "rgba(255,19,0,.8)", "fill": "rgba(255,19,0,.45)", "zIndex": 3 }).add()
  }))

  initTable()
})

const initTable = () => {
  $("#compareSchoolsAdmissions").dataTable({
    bPaginate: false,
    bInfo: false,
    order: [[5, "asc"]],
    aoColumnDefs: [
      { sType: "html", aTargets: [0] },
      { sType: "title-numeric", aTargets: [3] },
      { sType: "title-numeric", aTargets: [6] },
      { sType: "title-numeric", aTargets: [7] },
      { sType: "title-numeric", aTargets: [8] },
      { sType: "title-numeric", aTargets: [10] },
      { asSorting: ["asc", "desc"], aTargets: [0] },
      { asSorting: ["desc", "asc"], aTargets: ["_all"] },
    ],
    oLanguage: {
      sSearch: "Filter schools:",
    },
  })
}
</script>
